import { PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type { PipeInterface } from '@render/composables/usePipe'
import type { DeviceTimestamps } from '@render/lib/deviceTimestamps'

interface KlaviyoConfig {
  publicKey?: string
}

declare global {
  interface Window {
    klaviyo?: {
      track: (eventName: string, data?: Record<string, unknown>) => void
      identify: (data: Record<string, unknown>, callback?: () => void) => void
      isIdentified: () => Promise<boolean>
      push: (...args: unknown[]) => void
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _klOnsite?: unknown[]
  }
}

function getCookieValue(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))

  return match ? decodeURIComponent(match[1] ?? '') : ''
}

const utmRequiredFields = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fb_ads_id',
  'fb_adset_id',
]

function getUTMParams(pipeRef: PipeInterface | null): Record<string, unknown> {
  if (!pipeRef) return {}
  const trackingData = pipeRef.getData('var.global.trackingProperties') as
    | { queryParams?: Record<string, unknown> }
    | undefined
  const queryParams = trackingData?.queryParams ?? {}

  const result: Record<string, unknown> = {}
  for (const key of utmRequiredFields) {
    if (key in queryParams) {
      const parts = key.split('_')
      const camelKey = key.startsWith('utm_')
        ? parts
            .slice(1)
            .map((p, i) => (i === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
            .join('') + 'Utm'
        : key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      result[camelKey] = queryParams[key]
    }
  }

  return result
}
function generateUniqueId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`
}

function isKlaviyoAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.klaviyo === 'object' &&
    window.klaviyo !== null &&
    typeof window.klaviyo.track === 'function' &&
    typeof window.klaviyo.identify === 'function'
  )
}

function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms)
    promise.then((value) => {
      clearTimeout(timer)
      resolve(value)
    })
  })
}

// Bridges two calling conventions: klaviyo.js's own callback-style real API
// (identify(data, callback?) => void — no returned promise, per the type
// declaration above) vs. its temporary pre-load stub, which wraps every
// method in a Promise instead (confirmed via manual testing). `fn` is called
// once with a completion callback; if it also returns a thenable (the
// stub's shape), that's honored too — whichever settles first wins. Bounded
// by withTimeout so a real-client edge case where the callback never fires
// can't hang the caller forever.
function callAndAwait(fn: (done: (result: unknown) => void) => unknown, timeoutMs: number) {
  return withTimeout(
    new Promise<unknown>((resolve) => {
      const maybeThenable = fn((result) => resolve(result))
      if (maybeThenable && typeof (maybeThenable as Promise<unknown>).then === 'function') {
        ;(maybeThenable as Promise<unknown>).then(resolve).catch(() => resolve(undefined))
      }
    }),
    timeoutMs,
    undefined,
  )
}

const klaviyo: PipeIntegration = (): PipeIntegrationDef => {
  let pipeRef: PipeInterface | null = null
  let identifiedOverride: boolean | null = null

  function isKlaviyoEventAllowed(): boolean {
    const email = pipeRef?.getData('var.global.email') as string | undefined
    if (!email) return false

    // Gate on whether a purchase has actually completed, not on
    // var.global.lastPaymentId — that's populated straight off the checkout
    // SDK's own completion callback, and Yuno's SDK never hands the frontend
    // a payment id there (unlike Primer), so it's permanently empty for Yuno
    // purchases. deviceTimestamps.paymentCompleted (setDeviceTimestampOnce in
    // deviceTimestamps.ts) is set unconditionally by every checkout provider
    // at the same completion point — unlike purchasedPpos, it has no
    // dependency on a resolved ppoId — so it's the reliable provider-agnostic
    // signal.
    const deviceTimestamps = pipeRef?.getData('var.global.deviceTimestamps') as
      | DeviceTimestamps
      | undefined
    return !deviceTimestamps?.paymentCompleted
  }

  function getDefaultParams(): Record<string, unknown> {
    if (!pipeRef) return {}
    const constants = pipeRef.constants.value
    const distributionPath = (constants['system.distributionPath'] as string) ?? ''

    // pageCode / page: first segment after the distribution path prefix
    const afterPrefix = window.location.pathname.split(distributionPath)[1] ?? ''
    const pageSegment = afterPrefix.split('/').filter(Boolean)[0] ?? null

    return {
      /* eslint-disable @typescript-eslint/naming-convention */
      pageCode: pageSegment,
      page: pageSegment,
      pipe: distributionPath,
      pipe_id: pipeRef.data.value?.id ?? null,
      language: constants['system.language'] ?? null,
      geography: constants['system.geography'] ?? null,
      brand_code: constants['system.brandCode'] ?? null,
      brand: constants['system.brandCode'] ?? null,
      claritySessionID: getCookieValue('_clsk').split('^')[0] || null,
      eventSourceSystem: 'pipehouse',
      eventSourcePlatform: 'web',
      pipeSeries: 'pipehouse',
      pipe_series: 'pipehouse',
      // Ingress AB-test trace id (see seedOriginDefaults in usePipe.ts):
      // current = most recent ingress landing's id. `origin` is intentionally
      // omitted — only `current` is sent to Klaviyo.
      currentIngressTraceId: pipeRef.getData('var.system.currentIngressTraceId') ?? null,
    }
  }

  function mergeData(customData?: Record<string, unknown>): Record<string, unknown> {
    return {
      ...getDefaultParams(),
      ...(customData ?? {}),
      ...getUTMParams(pipeRef),
      /* eslint-disable @typescript-eslint/naming-convention */
      unique_id: generateUniqueId(),
      $event_id: generateUniqueId(),
      /* eslint-enable @typescript-eslint/naming-convention */
    }
  }

  // Events that fired before klaviyo.js became available, OR before
  // isKlaviyoEventAllowed()'s gate (email/paymentCompleted) was satisfied, get
  // parked here instead of trying to mimic Klaviyo's own internal pre-load
  // queue (window._klOnsite) — that protocol turned out to be an
  // undocumented, multi-stage internal detail (temp stub -> array-hybrid ->
  // real client) that's too fragile to reverse-engineer reliably. Simpler
  // and more trustworthy: wait until BOTH gates are genuinely satisfied, then
  // replay trackEvent() itself so every call goes through the exact same
  // path (identifyUser, gating, etc.) it would have on a normal, fast load.
  // Tradeoff: a visitor who never provides an email now sits in the queue
  // for up to 60s before being dropped, instead of an instant drop — same
  // end result (Klaviyo never bills for that visitor), just less eager to
  // give up, to accommodate email arriving a little later than the click.
  const pendingEvents: { eventName: string; customData?: Record<string, unknown> }[] = []
  let retryHandle: ReturnType<typeof setInterval> | null = null

  function scheduleRetry(): void {
    if (retryHandle !== null) return
    let ticks = 0
    retryHandle = setInterval(() => {
      ticks++
      if (isKlaviyoAvailable() && isKlaviyoEventAllowed()) {
        clearInterval(retryHandle!)
        retryHandle = null
        const queued = pendingEvents.splice(0, pendingEvents.length)
        queued.forEach(({ eventName, customData }) => {
          void trackEvent(eventName, customData)
        })
        return
      }
      // Klaviyo genuinely never loaded, or email never showed up — give up
      // rather than polling for the life of the tab.
      if (ticks >= 200) {
        clearInterval(retryHandle!)
        retryHandle = null
        pendingEvents.length = 0
      }
    }, 300)
  }

  async function trackEvent(
    eventName: string,
    customData?: Record<string, unknown>,
  ): Promise<void> {
    if (!isKlaviyoAvailable() || !isKlaviyoEventAllowed()) {
      pendingEvents.push({ eventName, customData })
      scheduleRetry()
      return
    }

    await identifyUser()
    window.klaviyo!.track(eventName, mergeData(customData))
  }

  async function isUserIdentified() {
    if (!isKlaviyoAvailable()) return false
    if (identifiedOverride !== null) return identifiedOverride

    // isIdentified() is a read, not a fire-and-forget write. Even once
    // isKlaviyoAvailable() is true, window.klaviyo may still be klaviyo.js's
    // own temporary pre-load stub (not the fully-booted real client) — if
    // that stub never gets around to answering, this would hang forever.
    // Bound it so identifyUser()/trackEvent() can't block indefinitely;
    // falls back to "not identified".
    return withTimeout(window.klaviyo!.isIdentified(), 4000, false)
  }

  async function identifyUser(customData?: Record<string, unknown>): Promise<void> {
    if (!isKlaviyoAvailable()) return

    const alreadyIdentified = await isUserIdentified()
    if (alreadyIdentified) return

    const email = pipeRef?.getData('var.global.email') as string | undefined
    const profile: Record<string, unknown> = mergeData(customData)

    if (email) profile['$email'] = email

    identifiedOverride = null
    // Actually wait for identify() to complete before trackEvent() fires the
    // subsequent track() call — previously this fired-and-forgot, so track()
    // could reach Klaviyo before the profile was associated with the email,
    // landing the event on the still-anonymous session instead.
    await callAndAwait((done) => window.klaviyo!.identify(profile, () => done(undefined)), 4000)
  }

  return {
    getHead(config: KlaviyoConfig) {
      const publicKey = config.publicKey ?? null
      if (!publicKey) return {}

      return {
        script: [
          {
            // Proxy stub — queues calls made before the async SDK resolves.
            innerHTML: `!(function(){if(!window.klaviyo){window._klOnsite=window._klOnsite||[];try{window.klaviyo=new Proxy({},{get:function(n,i){return"push"===i?function(){var n;(n=window._klOnsite).push.apply(n,arguments)}:function(){for(var n=arguments.length,o=new Array(n),w=0;w<n;w++)o[w]=arguments[w];var t="function"==typeof o[o.length-1]?o.pop():void 0,e=new Promise(function(n){window._klOnsite.push([i].concat(o,[function(i){t&&t(i),n(i)}]))});return e}}})}catch(n){(window.klaviyo=window.klaviyo||[]),(window.klaviyo.push=function(){var n;(n=window._klOnsite).push.apply(n,arguments)})}}})();`,
            tagPriority: 999,
          },
          {
            src: `https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${publicKey}`,
            async: true,
            tagPriority: 1000,
          },
        ],
      }
    },

    setup(pipe: PipeInterface, config: KlaviyoConfig) {
      if (typeof window === 'undefined') return
      pipeRef = pipe

      const publicKey = config.publicKey ?? null
      if (!publicKey) return
      pipe.constants.value['system.klaviyoPublicKey'] = publicKey

      pipe.subscribe(PAYMENT_EVENTS.resetUser, () => {
        document.cookie = '__kla_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        identifiedOverride = false
      })

      pipe.subscribe('navigation:show', async () => {
        await pipe.whenReady()
        await trackEvent('page viewed')
      })
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutRequested,
        async (payload: Record<string, unknown>) => {
          await trackEvent('system event', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutLoaded,
        async (payload: Record<string, unknown>) => {
          await trackEvent('system event', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutLoadedConfirmation,
        async (payload: Record<string, unknown>) => {
          await trackEvent('system event', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutBeforePaymentCreated,
        async (payload: Record<string, unknown>) => {
          await trackEvent('click', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingPlanSelectedSlimPayload,
        async (payload: Record<string, unknown>) => {
          payload.clickName = 'Plan Selected'
          await trackEvent('click', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCustomEvent,
        async (payload: { eventName: string; customData?: Record<string, unknown> }) => {
          if (!payload.eventName) return

          const customData = payload.customData ?? {}
          customData.clickName = payload.eventName
          await trackEvent('click', customData)
        },
      )
    },
  }
}

export default klaviyo
